/**
 * 接続を作る段階（CONNECTING）に待ち時間を付ける WebSocket を作る。
 * graphql-ws は接続途中のソケットを閉じず、待ち時間も持たない。詰まった試行は全購読で共有され、
 * ブラウザが諦めるまで以降の送信がすべて待たされる。OPEN にならない接続をここで閉じれば、
 * graphql-ws が自動で再接続し、待機中の購読はそのまま通る。
 */

import { type ConnectTimeoutScheduleOptions, createConnectTimeoutSchedule } from './connect-timeout-schedule';

export { DEFAULT_CONNECT_TIMEOUT_MS, MAX_CONNECT_TIMEOUT_MS } from './connect-timeout-schedule';

export type ConnectTimeoutInfo = {
  timeoutMs: number;
  consecutiveTimeouts: number;
};

export type ConnectTimeoutWebSocketOptions = ConnectTimeoutScheduleOptions & {
  webSocketImpl?: typeof WebSocket;
  onConnectTimeout?: (info: ConnectTimeoutInfo) => void;
};

/**
 * @remarks 閉じるのは OPEN になる前の接続だけで、サーバーに届いた要求は途中で切らない。
 */
export function createConnectTimeoutWebSocket({
  webSocketImpl,
  onConnectTimeout,
  ...scheduleOptions
}: ConnectTimeoutWebSocketOptions = {}): typeof WebSocket {
  const BaseWebSocket = webSocketImpl ?? WebSocket;
  const schedule = createConnectTimeoutSchedule(scheduleOptions);

  return class ConnectTimeoutWebSocket extends BaseWebSocket {
    constructor(url: string | URL, protocols?: string | string[]) {
      super(url, protocols);

      const timeoutMs = schedule.nextTimeoutMs();
      const timer = setTimeout(() => {
        if (this.readyState !== BaseWebSocket.CONNECTING) {
          return;
        }
        const consecutiveTimeouts = schedule.markTimedOut();
        onConnectTimeout?.({ timeoutMs, consecutiveTimeouts });
        this.close();
      }, timeoutMs);

      this.addEventListener('open', () => {
        clearTimeout(timer);
        schedule.markConnected();
      });
      this.addEventListener('close', () => clearTimeout(timer));
    }
  };
}
